import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import {
  CODOutstandingReport,
  CODOutstandingReportRequest,
  CODOutstandingReportResponse,
} from 'src/app/core/models/reports/cod-outstanding-report.model';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  FILE_NAME,
  OUTSTANDING_PDF_REPORT,
  OutstandingReportFormGroup,
} from './outstanding.constant';

@Component({
  selector: 'app-outstanding',
  standalone: false,
  templateUrl: './outstanding.component.html',
  styleUrl: './outstanding.component.scss',
})
export class OutstandingComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: OutstandingReportFormGroup = inject(MAT_DIALOG_DATA);
  private readonly userService = inject(UserService);
  private readonly dialogRef = inject(MatDialogRef<OutstandingComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly codsReportsService = inject(CodsReportsService);
  private readonly commonUtils = inject(CommonUtils);
  private readonly reportUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly PDF_REPORT = OUTSTANDING_PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  outstandingReportForm!: FormGroup;

  currDate!: Date;
  minDate!: Date;
  customMaxDate!: Date;
  toDate!: Date;
  outstandingReportResponse!: CODOutstandingReportResponse;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.customMaxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 1,
    );
    this.toDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 1,
    );

    this.outstandingReportForm = this.fb.nonNullable.group({
      fromDate: [this.toDate],
      toDate: [this.toDate],
      reportFormat: [Options.PDF],
    });
  }

  radioChange(newValue: Options) {
    this.outstandingReportForm.get('reportFormat')?.setValue(newValue);
  }

  clickExit() {
    this.commonUtils.openExitModal(this.outstandingReportForm.dirty);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof CollectedComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.outstandingReportForm.value;
          const locationcode = user?.location;
          const fromdate = this.commonUtils.getFormattedDate(
            formValue.fromDate,
          );
          const todate = this.commonUtils.getFormattedDate(formValue.toDate);

          const request: CODOutstandingReportRequest = {
            locationcode,
            fromdate,
            todate,
          };

          return this.codsReportsService.getCODOutstandingReport(request).pipe(
            take(1),
            map((data: CODOutstandingReportResponse) => {
              const formatType =
                this.outstandingReportForm.get('reportFormat')?.value;
              const CODOutstanding = data.response.CODCollectedCleared;
              if (CODOutstanding?.length) {
                if (formatType === Options.CSV) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      `${'COD Outstanding'}`,
                      '',
                      '',
                      '',
                      '',
                      '',
                    ],
                    [
                      '',
                      '',
                      'Location:',
                      '',
                      `${data.response.location}`,
                      '',
                      '',
                      'Turn in ',
                      '',
                      `${data.response.turninDate}`,
                    ],
                  ];
                  const formatedDate = formatDate(
                    new Date(),
                    'MM-dd-yyyy',
                    'en-US',
                  );

                  const CSV_FILE_COLUMN_HEADING = [
                    'Driver'.toUpperCase(),
                    'Loop'.toUpperCase(),
                    'Trackno'.toUpperCase(),
                    'Customer'.toUpperCase(),
                    'City'.toUpperCase(),
                    'Expected'.toUpperCase(),
                    'CA/CH/C',
                    'D/P',
                    'TurnedIn'.toUpperCase(),
                    'Comments'.toUpperCase(),
                    'Date'.toUpperCase(),
                    'LocationCode'.toUpperCase(),
                  ];
                  const projectedRows = CODOutstanding.map(
                    (data: CODOutstandingReport) => ({
                      Driver: data.driver ?? '',
                      Loop: data.loop ?? '',
                      TrackingNumber: data.trackno ?? '',
                      Customer: data.customer ?? '',
                      City: data.city ?? '',
                      Expected: data.expected ?? '',
                      'CA/CH/C': data['ca/ch/cc'] ?? '',
                      'D/P': data['d/p'] ?? '',
                      TurnedIn: data.turnedin ?? '',
                      Comments: data.comments ?? '',
                      Date: data.date ?? '',
                      LocationCode: data.locationcode ?? '',
                    }),
                  );

                  const customFooter = [
                    [],
                    [
                      'Total Expected ',
                      '',
                      `${data?.response.TotalExpectedCOD}`,
                      '',
                      '',
                      '',
                      'Total Turned In',
                      '',
                      `${data?.response.TotalTurnedInCOD}`,
                    ],
                    ['Printed:', `${formatedDate}`],
                  ];
                  this.reportUtils.exportCSVReport(
                    projectedRows,
                    FILE_NAME,
                    customHeader,
                    customFooter,
                    CSV_FILE_COLUMN_HEADING,
                  );
                }
                if (formatType === Options.PDF) {
                  this.outstandingReportResponse = data;
                  setTimeout(() => {
                    const content =
                      this.pdfContainer.element.nativeElement.innerHTML;
                    this.reportUtils.exportPdfReport(
                      content,
                      OUTSTANDING_PDF_REPORT.NAME,
                      FILE_NAME,
                    );
                  }, 0);
                }
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
              }
              this.dialogRef.close();
            }),
          );
        }),
      )
      .subscribe();
  }
}
